// src/components/Layout/Header.tsx
// UPDATED: 2026-03-27
// ✅ Modal de login com design refinado
// ✅ Fix: navigate('/login/filiado') lowercase consistente com App.tsx

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Menu, X, LogIn, MessageCircle, GraduationCap, Building2, ArrowRight } from 'lucide-react';

interface MenuItem    { label: string; path?: string; subItems?: SubMenuItem[] }
interface SubMenuItem { label: string; path: string; description?: string }

const WHATSAPP_MESSAGE = encodeURIComponent('Olá! Gostaria de mais informações sobre os serviços da Federação.');

const Header: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen]   = useState(false);
  const [activeDropdown, setActiveDropdown]   = useState<string | null>(null);
  const [loginModalOpen, setLoginModalOpen]   = useState(false);

  const menuItems: MenuItem[] = [
    { label: 'Início', path: '/' },
    {
      label: 'Institucional',
      subItems: [
        { label: 'História',                 path: '/institucional/historia',    description: '70+ anos de tradição'   },
        { label: 'Atribuições',              path: '/institucional/atribuicoes', description: 'Nossa missão'           },
        { label: 'Diretoria',                path: '/institucional/diretoria',   description: 'Liderança'              },
        { label: 'LGPD',                     path: '/institucional/lgpd',        description: 'Privacidade'            },
        { label: 'Comissões de Avaliações',  path: '/institucional/comissoes',   description: 'Avaliação técnica'      },
      ],
    },
    {
      label: 'Filiação',
      subItems: [
        { label: 'Como Filiar-se',             path: '/filiacao',                 description: 'Torne-se um Filiado' },
       { label: 'Verificação de Filiação',   path: '/verificar',     description: 'Confirmação de filiação' },
         { label: 'Consulte Profissionais',        path: '/publicacoes/profissionais', description: 'Destaque da área'  },
       { label: 'Classificação Profissional', path: '/filiacao/classificacao',   description: 'Categorias'          },
        
      ],
    },
    {
      label: 'Serviços',
      subItems: [
       

        { label: 'Cosmetólogo Internacional', path: '/servicos/cosmetologo', description: 'Certificação'       },
        { label: 'Revalida Pro',              path: '/servicos/revalida',    description: 'Obtenha seu Revalida' },
        { label: 'Site Profissional',         path: '/servicos/site',        description: 'Site personalizado'  },
      ],
    },
    {
      label: 'Cursos',
      subItems: [
        { label: 'Cursos da Federação', path: '/cursos', description: 'Cursos oficiais' },
      ],
    },
    {
      label: 'Publicações',
      subItems: [
        { label: 'Notícias & Eventos',             path: '/noticias',      description: 'Últimas novidades'  },
        { label: 'Homologações',         path: '/publicacoes/homologacoes',  description: 'Cursos homologados' },
         { label: 'Ações Sociais',        path: '/acoes-sociais',              description: 'Iniciativas da Federação' }, // ← NOVO

      ],
    },
    { label: 'Escolas',       path: '/escolas'       },
    { label: 'Fale Conosco',  path: '/fale-conosco'  },
  ];

  const toggleDropdown    = (label: string) => setActiveDropdown(activeDropdown === label ? null : label);
  const handleWhatsApp    = () => window.open(`https://wa.me/5511999733976?text=${WHATSAPP_MESSAGE}`, '_blank');
  const handleLoginClick  = () => { setLoginModalOpen(true); setMobileMenuOpen(false); };

  return (
    <>
      {/* ── MAIN HEADER ──────────────────────────────────────────────── */}
      <header className="bg-white shadow-lg sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-4 md:px-6">
          <div className="flex items-center justify-between h-20 gap-2 min-h-[80px]">
 
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 flex items-center h-16 min-w-fit">
              <img src="/imagem/Bras.png" alt="Federação dos Profissionais da Beleza"
                className="h-16 w-auto object-contain hover:opacity-80 transition-opacity duration-300"/>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden xl:flex items-center gap-0 flex-1 justify-center">
              {menuItems.map(item => (
                <div key={item.label} className="relative group"
                  onMouseEnter={() => item.subItems && setActiveDropdown(item.label)}
                  onMouseLeave={() => setActiveDropdown(null)}>
                  {item.path ? (
                    <Link to={item.path}
                      className="px-3 xl:px-4 py-2 text-cosmos-800 font-semibold hover:text-gold-600 transition-colors flex items-center gap-1 text-sm whitespace-nowrap">
                      {item.label}
                    </Link>
                  ) : (
                    <button className="px-3 xl:px-4 py-2 text-cosmos-800 font-semibold hover:text-gold-600 transition-colors flex items-center gap-1 text-sm whitespace-nowrap">
                      {item.label}
                      {item.subItems && <ChevronDown className={`w-4 h-4 transition-transform ${activeDropdown === item.label ? 'rotate-180' : ''}`}/>}
                    </button>
                  )}
                  {item.subItems && (
                    <div className={`absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-2xl border border-gold-200 overflow-hidden transition-all duration-200 ${activeDropdown === item.label ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
                      {item.subItems.map(sub => (
                        <Link key={sub.path} to={sub.path}
                          className="block px-5 py-3 hover:bg-gold-50 transition-colors border-b border-gray-100 last:border-0 group"
                          onClick={() => setActiveDropdown(null)}>
                          <div className="font-semibold text-cosmos-800 group-hover:text-gold-600 transition-colors text-sm">{sub.label}</div>
                          {sub.description && <div className="text-xs text-gray-500 mt-0.5">{sub.description}</div>}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* CTAs */}
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <button onClick={handleLoginClick}
                className="btn-outline-gold px-3 sm:px-4 py-2 text-xs sm:text-sm flex items-center gap-2 whitespace-nowrap hover:scale-105 transition-transform">
                <LogIn className="w-4 h-4 flex-shrink-0"/>
                <span className="hidden sm:inline">Login</span>
              </button>
              <button onClick={handleWhatsApp}
                className="btn-whatsapp-luxury px-3 sm:px-4 py-2 text-xs sm:text-sm flex items-center gap-2 whitespace-nowrap hover:scale-105 transition-transform">
                <MessageCircle className="w-4 h-4 flex-shrink-0"/>
                <span className="hidden sm:inline">WhatsApp</span>
              </button>
            </div>

            {/* Mobile toggle */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="xl:hidden p-2 text-cosmos-800 hover:text-gold-600 transition-colors flex-shrink-0">
              {mobileMenuOpen ? <X className="w-7 h-7"/> : <Menu className="w-7 h-7"/>}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="xl:hidden bg-white border-t border-gray-200 shadow-xl">
            <nav className="container mx-auto px-3 sm:px-4 py-4 max-h-[70vh] overflow-y-auto">
              {menuItems.map(item => (
                <div key={item.label} className="border-b border-gray-100 last:border-0">
                  {item.path ? (
                    <Link to={item.path}
                      className="block py-3 text-cosmos-800 font-semibold hover:text-gold-600 transition-colors text-sm"
                      onClick={() => setMobileMenuOpen(false)}>
                      {item.label}
                    </Link>
                  ) : (
                    <>
                      <button onClick={() => toggleDropdown(item.label)}
                        className="w-full flex items-center justify-between py-3 text-cosmos-800 font-semibold hover:text-gold-600 transition-colors text-sm">
                        <span>{item.label}</span>
                        <ChevronDown className={`w-5 h-5 transition-transform flex-shrink-0 ${activeDropdown === item.label ? 'rotate-180' : ''}`}/>
                      </button>
                      {activeDropdown === item.label && item.subItems && (
                        <div className="pl-4 pb-2">
                          {item.subItems.map(sub => (
                            <Link key={sub.path} to={sub.path}
                              className="block py-2 text-xs sm:text-sm text-gray-600 hover:text-gold-600 transition-colors"
                              onClick={() => { setMobileMenuOpen(false); setActiveDropdown(null); }}>
                              {sub.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              <div className="flex flex-col gap-3 pt-4 mt-4 border-t border-gray-200">
                <button onClick={handleLoginClick}
                  className="btn-outline-gold w-full py-3 flex items-center justify-center gap-2 text-sm">
                  <LogIn className="w-5 h-5 flex-shrink-0"/> Login Aluno/Filiado
                </button>
                <button onClick={handleWhatsApp}
                  className="btn-whatsapp-luxury w-full py-3 flex items-center justify-center gap-2 text-sm">
                  <MessageCircle className="w-5 h-5 flex-shrink-0"/> WhatsApp
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* ── MODAL DE LOGIN ───────────────────────────────────────────── */}
      {loginModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setLoginModalOpen(false)}>
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"/>

          {/* Card */}
          <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}>

            {/* Topo decorativo */}
            <div className="h-1.5 w-full bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600"/>

            {/* Fechar */}
            <button onClick={() => setLoginModalOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
              <X className="w-4 h-4 text-gray-500"/>
            </button>

            <div className="px-7 pt-7 pb-8">
              {/* Logo + título */}
              <div className="text-center mb-7">
                <img src="/imagem/Bras.png" alt="Federação" className="h-14 w-auto object-contain mx-auto mb-4 opacity-90"/>
                <h2 className="text-xl font-black text-gray-900">Área Restrita</h2>
                <p className="text-sm text-gray-500 mt-1">Escolha sua área de acesso</p>
              </div>

              {/* Botão Aluno */}
              <button
                onClick={() => { navigate('/login/aluno'); setLoginModalOpen(false); }}
                className="w-full group relative overflow-hidden rounded-2xl p-px mb-3 transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #1e40af, #3b82f6)' }}
              >
                <div className="flex items-center gap-4 px-5 py-4 rounded-2xl bg-gradient-to-r from-blue-700 to-blue-500 text-white">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="w-5 h-5"/>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-black text-sm">Portal do Aluno</p>
                    <p className="text-xs text-blue-200 mt-0.5">Certificados, cursos e diplomas</p>
                  </div>
                  <ArrowRight className="w-4 h-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all"/>
                </div>
              </button>

              {/* Botão Filiado */}
              <button
                onClick={() => { navigate('/login/filiado'); setLoginModalOpen(false); }}
                className="w-full group relative overflow-hidden rounded-2xl p-px mb-6 transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg, #92400e, #d97706)' }}
              >
                <div className="flex items-center gap-4 px-5 py-4 rounded-2xl text-gray-900"
                  style={{ background: 'linear-gradient(135deg, #f4d03f, #d4af37)' }}>
                  <div className="w-10 h-10 rounded-xl bg-black/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-amber-900"/>
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-black text-sm text-amber-950">Portal do Filiado</p>
                    <p className="text-xs text-amber-800 mt-0.5">Gerencie sua escola e alunos</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-amber-800 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all"/>
                </div>
              </button>

              {/* Rodapé */}
              <div className="text-center border-t border-gray-100 pt-5">
                <p className="text-xs text-gray-500 mb-2">Ainda não é Filiado?</p>
                <Link to="/filiacao" onClick={() => setLoginModalOpen(false)}
                  className="text-amber-600 font-bold text-sm hover:text-amber-700 transition-colors">
                  Cadastre-se agora →
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;